import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, Music2, Phone, Mail, MapPin } from "lucide-react";
import { CONTACT, NAV_LINKS, LOGO_URL } from "../mock";

const ICONS = {
  Facebook,
  Instagram,
  LinkedIn: Linkedin,
  YouTube: Youtube,
  TikTok: Music2,
};

export default function Footer() {
  return (
    <footer className="bg-brand-900 text-brand-100">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <div className="bg-white/95 rounded-2xl p-4 inline-block">
              <img src={LOGO_URL} alt="AwareHub TMS" className="h-14 w-auto object-contain" />
            </div>
            <p className="mt-5 text-sm leading-relaxed text-brand-200 max-w-xs">
              A holistic, multi-disciplinary mental health clinic offering TMS for
              depression, anxiety and OCD in Sydney &amp; Bundaberg. Esketamine
              available for resistant depression.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {CONTACT.socials.map((s) => {
                const Icon = ICONS[s.label] || Music2;
                return (
                  <a
                    key={s.label}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="h-10 w-10 rounded-full bg-white/10 hover:bg-brand-500 flex items-center justify-center transition-colors duration-200"
                  >
                    <Icon className="h-4.5 w-4.5" strokeWidth={1.8} />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-white font-display text-lg mb-4">Explore</h4>
            <ul className="space-y-2.5">
              {NAV_LINKS.filter((l) => l.path !== "/faq" && l.path !== "/news").map((l) => (
                <li key={l.path}>
                  <Link
                    to={l.path}
                    className="text-sm text-brand-200 hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <h4 className="text-white font-display text-lg mb-4">Our Clinics</h4>
            <div className="grid sm:grid-cols-2 gap-6">
              {CONTACT.locations.map((loc) => (
                <div key={loc.address}>
                  <p className="flex items-start gap-2 text-sm text-brand-200">
                    <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{loc.address}</span>
                  </p>
                  <a
                    href={`mailto:${loc.email}`}
                    className="mt-2 flex items-center gap-2 text-sm text-brand-200 hover:text-white transition-colors"
                  >
                    <Mail className="h-4 w-4 shrink-0" />
                    {loc.email}
                  </a>
                </div>
              ))}
            </div>
            <a
              href={CONTACT.phoneHref}
              className="mt-6 inline-flex items-center gap-2 text-white font-semibold"
            >
              <Phone className="h-4 w-4" />
              {CONTACT.phone}
            </a>

            {/* Trading hours — wider block under Our Clinics */}
            <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="footer-hours">
              <h5 className="text-white font-semibold text-xs tracking-[0.2em] uppercase mb-3">Trading Hours</h5>
              <div className="flex flex-wrap gap-x-10 gap-y-2">
                {CONTACT.hours.map((h) => (
                  <div key={h.day} className="flex items-baseline gap-2">
                    <span className="text-sm text-brand-200">{h.day}</span>
                    <span className="text-sm text-white/90 font-medium">{h.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-brand-300">
            Copyright © 2026 AwareHub Pty Ltd - All Rights Reserved. *CONDITIONS APPLY
          </p>
          <div className="flex items-center gap-5">
            <a
              href="https://awarehub.com.au/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-brand-300 hover:text-white transition-colors"
            >
              Privacy Policy
            </a>
            <a
              href="https://awarehub.com.au/cookies-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-brand-300 hover:text-white transition-colors"
            >
              Cookie Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
